import { BreakpointObserver } from "@angular/cdk/layout";
import { ChangeDetectionStrategy, Component, Inject, OnInit, PLATFORM_ID, signal, WritableSignal } from "@angular/core";
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, ValidationErrors, Validators } from "@angular/forms";
import { MatSnackBar } from "@angular/material/snack-bar";
import { MatStepper, MatStepperModule, StepperOrientation } from "@angular/material/stepper";
import { Observable, Subscription } from "rxjs";

import { AsyncPipe, isPlatformBrowser } from "@angular/common";
import { MatButtonModule } from "@angular/material/button";
import { MatNativeDateModule } from "@angular/material/core";
import { MatDialog, MatDialogActions, MatDialogClose, MatDialogContent } from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { first, map } from "rxjs/operators";
import { ConfigService } from "src/app/servicios/config/config.service";
import { ModalLoginComponent } from "../modal-login/modal-login.component";
import { AuthService } from "@src/app/servicios/auth.service";
import { GoogleSigninButtonModule, SocialAuthService } from "@abacritt/angularx-social-login";
import { PlatformService } from "@src/app/servicios/platform/platform.service";
import { Router } from "@angular/router";
import { environment } from "@src/environments/environment";
import { CookieService } from "ngx-cookie-service";

@Component({
  selector: "app-modal-registro",
  templateUrl: "./modal-registro.component.html",
  styleUrls: ["./modal-registro.component.css"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AsyncPipe, FormsModule, FormsModule, MatButtonModule, MatDialogActions, MatDialogClose, MatDialogContent, MatFormFieldModule, MatIconModule, MatInputModule, MatNativeDateModule, MatStepperModule, ReactiveFormsModule, GoogleSigninButtonModule],
})
export class ModalRegistroComponent implements OnInit {
  isShow: boolean = false;
  isSocialLogin = signal(false);
  googleClientId: WritableSignal<string> = signal("");
  googleCallbackUrl: WritableSignal<string> = signal("");
  twitterClientId: WritableSignal<string> = signal("");
  twitterClientSecret: WritableSignal<string> = signal("");
  googleLoginSub: Subscription;

  emailFormControl = new FormControl("", [Validators.required, Validators.email]);
  passwordFormControl = new FormControl("", [Validators.required, Validators.minLength(6)]);
  firstFormGroup: FormGroup;
  secondFirstGroup: FormGroup;

  msgErrores = {
    required: "es obligatorio",
    minlength: "debe ser mayor de 6 caracteres",
    email: "debe tener el formato correcto",
  };
  stepperOrientation: Observable<StepperOrientation>;
  constantesGlobales: any;

  constructor(
    private _formBuilder: FormBuilder,
    private authService: AuthService,
    private _snackBar: MatSnackBar,
    breakpointObserver: BreakpointObserver,
    public dialog: MatDialog,
    private configService: ConfigService,
    @Inject(PLATFORM_ID) private platformId,
    private platformService: PlatformService,
    private router: Router,
    private cookieService: CookieService,
    private socialAuth: SocialAuthService,
  ) {
    this.firstFormGroup = this._formBuilder.group({
      nombre: "",
      apellidos: "",
      email: this.emailFormControl,
      check: false,
    });

    this.secondFirstGroup = this._formBuilder.group({
      pass: this.passwordFormControl,
      pass2: this.passwordFormControl,
    });

    if (breakpointObserver.observe("(min-width: 800px)")) {
      this.stepperOrientation = breakpointObserver.observe("(min-width: 800px)").pipe(map(({ matches }) => (matches ? "horizontal" : "vertical")));
    }
    this.constantesGlobales = this.configService.getConstantesGlobales();
  }

  ngOnInit(): void {
    if (this.socialAuth.initState) {
      this.socialAuth.initState.pipe(first()).subscribe((initialized) => {
        if (!initialized) return;
        this.initializeSocialVariables();
      });
    }
  }

  goForward_step1(stepper: MatStepper) {
    this.getFormValidationErrors(this.firstFormGroup);

    const checkMail = this.authService.checkMail(this.firstFormGroup.get(["email"]).value);
    if (checkMail) {
      checkMail.pipe(first()).subscribe((result) => {
        if (result == false) {
          stepper.next();
        } else {
          this._snackBar.open("El mail ya existe", "OK", {
            duration: 3000,
          });
        }
      });
    }
  }

  getFormValidationErrors(form) {
    Object.keys(form.controls).forEach((key) => {
      const controlErrors: ValidationErrors = form.get(key).errors;
      if (controlErrors != null) {
        Object.keys(controlErrors).forEach((keyError) => {
          //console.log(keyError);
          this._snackBar.open("El campo " + key + " " + this.msgErrores[keyError], "OK", {
            duration: 3000,
          });
          return;
        });
      }
    });
  }

  goForward_step2(stepper: MatStepper) {
    this.getFormValidationErrors(this.secondFirstGroup);
    if (this.secondFirstGroup.get(["pass"]).value === this.secondFirstGroup.get(["pass2"]).value) {
      const { nombre, apellidos, email, pass } = Object.assign(this.firstFormGroup.value, this.secondFirstGroup.value);

      const newUser = this.authService.newUser(nombre, apellidos, email, pass, this.constantesGlobales.idAmbito);
      if (newUser) {
        newUser.pipe(first()).subscribe((result) => {
          if (result["status"] == "OK") {
            stepper.next();
          } else {
            this._snackBar.open(result["message"], "OK", {
              duration: 3000,
            });
          }
        });
      }
    } else {
      this._snackBar.open("La contraseña no coincide", "OK", {
        duration: 3000,
      });
    }
  }

  openLogin(): void {
    const dialogRef = this.dialog.open(ModalLoginComponent, {
      panelClass: "modal-login",
      maxWidth: "85%",
    });

    dialogRef.afterClosed().subscribe(() => {
      // console.log(`Dialog result: ${result}`);
    });
  }

  initializeSocialVariables() {
    const configPrensa = this.configService.getConfigPrensa();
    if (configPrensa) {
      configPrensa.pipe(first()).subscribe((config) => {
        const globalConfig = config["general"]["global-constants"];
        if (!globalConfig) return;

        const socialLoginConfig = globalConfig["social-login"];
        if (!socialLoginConfig) return;

        this.googleClientId.set(globalConfig["social-login-google-client-id"]);
        this.googleCallbackUrl.set(globalConfig["social-login-google-callback-url"]);

        const isGoogleEnabled = Boolean(this.googleClientId && this.googleCallbackUrl);

        if (isGoogleEnabled) this.initializeGoogleSubscription();

        this.twitterClientId.set(globalConfig["social-login-twitter-client-id"]);
        this.twitterClientSecret.set(globalConfig["social-login-twitter-client-secret"]);
        const isTwitterEnabled = Boolean(this.twitterClientId && this.twitterClientSecret);

        this.isSocialLogin.set(isGoogleEnabled || isTwitterEnabled);
      });
    }
  }

  initializeGoogleSubscription() {
    this.googleLoginSub = this.socialAuth.authState.subscribe((user) => {
      if (user) {
        const googleLogin = this.authService.sendGoogleLogin(user.idToken);
        if (googleLogin) {
          googleLogin.pipe(first()).subscribe({
            next: (result) => {
              if (!result) return;
              if (result.message) {
                this._snackBar.open(result.message, "", {
                  duration: 2000,
                  horizontalPosition: "center",
                  verticalPosition: "top",
                  panelClass: ["snackbar-success"],
                });
              }
              if (this.platformService.isBrowser()) {
                this.setUserCookie(result.data, 60 * 60 * 24 * 30);
              }
              if (this.router.url === "/login") {
                if (isPlatformBrowser(this.platformId)) {
                  window.location.replace("/");
                }
              } else {
                if (isPlatformBrowser(this.platformId)) {
                  window.location.reload();
                }
              }
            },
            error: (response) => {
              this._snackBar.open(response.error.message, "OK", {
                duration: 2000,
                horizontalPosition: "center",
                verticalPosition: "top",
                panelClass: ["snackbar-error"],
              });
            },
          });
        }
      }
    });
  }
  /**
   * Función para guardar el usuario en una cookie
   *
   * @param {any} value - Usuario a guardar
   * @param {number} expiration - Tiempo de expiración de la cookie. Por defecto 30 días.
   * @returns void
   */
  setUserCookie(value, expiration = 60 * 60 * 24 * 30): void {
    if (!this.platformService.isBrowser()) return;

    this.cookieService.set("userLogin" + environment.name, JSON.stringify(value), expiration);
  }
}
